import type { OrgMember, OrgMemberRole } from "@solomon-apps/shared";
import { Button } from "@solomon-apps/shared/src/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogTitle
} from "@solomon-apps/shared/src/components/ui/dialog";
import { Input } from "@solomon-apps/shared/src/components/ui/input";
import {
    InputGroup,
    InputGroupAddon,
    InputGroupInput
} from "@solomon-apps/shared/src/components/ui/input-group";
import { Text } from "@solomon-apps/shared/src/components/ui/text";
import {
    ToggleGroup,
    ToggleGroupItem
} from "@solomon-apps/shared/src/components/ui/toggle-group";
import { CrossIcon } from "@solomon-labs/design/components/icons";
import { ButtonIcon, Label } from "@solomon-labs/design/components/ui";
import React, { useEffect, useMemo, useState } from "react";

import { useViewOnly } from "@/hooks/useViewOnly";

import { inviteEmailProblem, ROLE_DESCRIPTIONS, ROLE_ORDER } from "../teamData";

interface EditInvitationDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /**
     * Called once the form passes local validation; the parent re-issues the
     * invitation (fix-invite) and reports a failure through `error`.
     */
    onSubmit: (input: InviteInput) => void;
    isSubmitting?: boolean;
    /** A failure of the last submit, shown inline. */
    error?: Error | null;
    /** The pending member whose invitation is being edited. */
    member: OrgMember;
    /** Addresses already on the team, held back before sending. */
    existingEmails?: string[];
    /**
     * Where focus lands on dismiss. Opened from a page button, never a
     * Dialog trigger, so Base UI has no trigger to return to.
     */
    finalFocus?: () => HTMLElement | boolean;
}

export interface InviteInput {
    email: string;
    fullName: string | undefined;
    orgRole: OrgMemberRole;
}

/** Invite links last as long as a resent one (see the Resend dialog). */
const LINK_VALIDITY = "Links are valid for 7 days.";

/**
 * Edit Invitation dialog, same shell as `TeamMemberActionDialog`: bordered
 * header, body, then the primary action beside Cancel. One address, the
 * optional name and the role, pre-filled from the pending member; saving
 * re-issues the invitation (a fresh link is generated server-side).
 *
 * New members are added on their own page, `/team/new`
 * (`TeamAddMemberPage`), not here.
 */
export function EditInvitationDialog({
    open,
    onOpenChange,
    onSubmit,
    isSubmitting = false,
    error,
    member,
    existingEmails = [],
    finalFocus
}: EditInvitationDialogProps): React.ReactElement {
    // Mobile renders the dialog as a full-height bottom drawer with stacked,
    // touch-sized actions; desktop keeps the centered modal. Gated in JS
    // because the drawer positioning + button variant/size aren't expressible
    // as pure `md:` overrides.
    const isMobile = useViewOnly();
    const [email, setEmail] = useState("");
    const [fullName, setFullName] = useState("");
    const [role, setRole] = useState<OrgMemberRole>(member.orgRole);
    // Defer the address hint until the field is blurred so it doesn't flash
    // while the user is still typing.
    const [emailTouched, setEmailTouched] = useState(false);

    // Seed the form from the pending member whenever the dialog reopens so
    // stale input doesn't surface.
    useEffect(() => {
        if (open) {
            setEmail(member.email);
            setFullName(member.fullName ?? "");
            setRole(member.orgRole);
            setEmailTouched(false);
        }
    }, [open, member]);

    // The member's own address is not "already on the team" here.
    const taken = useMemo(
        () =>
            existingEmails.filter(
                (e) => e.toLowerCase() !== member.email.toLowerCase()
            ),
        [existingEmails, member]
    );
    const problem = inviteEmailProblem(email, taken);
    const showProblem = emailTouched && problem !== null;
    const canSubmit = problem === null && !isSubmitting;

    const handleSubmit = (e: React.FormEvent): void => {
        e.preventDefault();
        setEmailTouched(true);
        if (!canSubmit) return;
        onSubmit({
            email: email.trim(),
            fullName: fullName.trim() || undefined,
            orgRole: role
        });
    };

    return (
        <Dialog
            open={open}
            onOpenChange={(next) => {
                if (!next && !isSubmitting) onOpenChange(false);
            }}
        >
            <DialogContent
                finalFocus={finalFocus}
                className={
                    isMobile
                        ? // Full-height bottom drawer: pin to all edges, cancel
                          // the centered-modal translate, slide up from bottom.
                          // A full-height sheet is movement: 300ms named
                          // out-quart, not the dialog's 100ms fade, and no
                          // slide under reduced motion (rule: motion/durations).
                          "inset-0 max-w-none translate-x-0 translate-y-0 flex flex-col gap-0 overflow-hidden p-0 data-open:slide-in-from-bottom-full data-closed:slide-out-to-bottom-full duration-300 ease-[var(--ease-out-quart)] motion-reduce:animate-none"
                        : // why: the shared dialog width for Team dialogs.
                          "max-h-[88vh] max-w-[480px] gap-0 overflow-hidden p-0"
                }
            >
                <form
                    onSubmit={handleSubmit}
                    className={
                        isMobile
                            ? "flex flex-1 min-h-0 flex-col"
                            : "flex min-h-0 flex-col"
                    }
                >
                    <InviteDialogHeader
                        title="Edit Invitation"
                        closeDisabled={isSubmitting}
                        onClose={() => onOpenChange(false)}
                    />

                    {/* Body */}
                    <div
                        className={`overflow-y-auto px-4 md:px-6 pt-5 pb-10 md:pb-5 flex flex-col gap-5${
                            isMobile ? " flex-1 min-h-0" : ""
                        }`}
                    >
                        <div className="flex flex-col gap-1.5 shrink-0">
                            <LabeledTextField
                                isMobile={isMobile}
                                id="invite-email"
                                label="Email"
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                onBlur={() => setEmailTouched(true)}
                                state={showProblem ? "error" : undefined}
                                aria-describedby="invite-email-hint"
                                disabled={isSubmitting}
                                autoFocus
                            />
                            {/* Always rendered so the field block keeps a
                                constant height: the hint turning into an
                                error never shifts the rest of the form. */}
                            <Text
                                variant="small"
                                render={<p id="invite-email-hint" />}
                                className={
                                    showProblem
                                        ? "text-error-base"
                                        : "text-foreground-muted"
                                }
                            >
                                {showProblem ? problem : " "}
                            </Text>
                        </div>

                        <LabeledTextField
                            isMobile={isMobile}
                            id="invite-name"
                            label="Name"
                            type="text"
                            value={fullName}
                            onChange={(e) => setFullName(e.target.value)}
                            disabled={isSubmitting}
                        />

                        <LabeledField id="invite-role" label="Role">
                            {/* Stacked role cards: label + description
                                inside each toggle, so the role is explained
                                where it is chosen. */}
                            <ToggleGroup
                                variant="outline"
                                size="sm"
                                spacing={2}
                                orientation="vertical"
                                className="w-full flex-col items-stretch"
                                value={[role]}
                                onValueChange={(value) => {
                                    const next = value[0] as
                                        | OrgMemberRole
                                        | undefined;
                                    if (next) setRole(next);
                                }}
                            >
                                {ROLE_ORDER.map((r) => (
                                    <ToggleGroupItem
                                        key={r}
                                        value={r}
                                        disabled={isSubmitting}
                                        className="h-auto w-full items-start justify-start px-4 py-3 text-left"
                                    >
                                        <span className="flex flex-col items-start gap-1 text-left whitespace-normal">
                                            <Text
                                                variant="small"
                                                render={<span />}
                                            >
                                                {ROLE_DESCRIPTIONS[r].label}
                                            </Text>
                                            <Text
                                                variant="fine-print"
                                                render={<span />}
                                                className="opacity-70"
                                            >
                                                {ROLE_DESCRIPTIONS[r].headline}
                                            </Text>
                                        </span>
                                    </ToggleGroupItem>
                                ))}
                            </ToggleGroup>
                        </LabeledField>

                        <Text
                            variant="small"
                            render={<p />}
                            className="text-foreground-muted"
                        >
                            Saving withdraws the current invitation and creates
                            a new one. {LINK_VALIDITY}
                        </Text>

                        {error && (
                            <Text
                                variant="small"
                                render={<p />}
                                className="text-error-base"
                            >
                                {error.message}
                            </Text>
                        )}
                    </div>

                    {/* Footer: side-by-side actions on desktop; on mobile the
                        actions stack full-width and Cancel becomes a sunken
                        button (not a text link). */}
                    <div className="px-4 md:px-6 pt-2 md:pt-8 pb-5">
                        <div className="flex flex-col md:flex-row md:items-center gap-3">
                            <Button
                                variant="raised"
                                interaction="lifted"
                                size={isMobile ? "lg" : "md"}
                                fullWidth={isMobile}
                                type="submit"
                                disabled={!canSubmit}
                            >
                                {isSubmitting
                                    ? "Creating..."
                                    : "Create New Invitation"}
                            </Button>
                            <Button
                                variant={isMobile ? "sunken" : "transparent"}
                                size={isMobile ? "lg" : "md"}
                                interaction={isMobile ? "lifted" : undefined}
                                outline={isMobile}
                                fullWidth={isMobile}
                                type="button"
                                className={isMobile ? undefined : "underline"}
                                disabled={isSubmitting}
                                onClick={() => onOpenChange(false)}
                            >
                                Cancel
                            </Button>
                        </div>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}

/* ── Header ─────────────────────────────────────────────────── */

/**
 * The catalog DialogTitle carries the title so the dialog is named by it
 * (aria-labelledby); the close is the DS ButtonIcon + CrossIcon.
 */
function InviteDialogHeader({
    title,
    closeDisabled,
    onClose
}: {
    title: string;
    closeDisabled: boolean;
    onClose: () => void;
}): React.ReactElement {
    return (
        <div className="border-b border-line-base px-4 md:px-6 pt-6 pb-4">
            <div className="flex items-center justify-between gap-4">
                <DialogTitle variant="h2">{title}</DialogTitle>
                <ButtonIcon
                    variant="transparent"
                    size="lg"
                    icon={<CrossIcon aria-hidden="true" />}
                    aria-label="Close"
                    disabled={closeDisabled}
                    onClick={onClose}
                    className="-mr-1 h-7 w-7 shrink-0 md:h-8 md:w-8"
                />
            </div>
        </div>
    );
}

/* ── Text field: inline label (mobile) / stacked label (desktop) ── */

/**
 * Email/Name input. On mobile the field title sits inside the field
 * (inline-start, matching the Settings inline-input format); on desktop
 * it stacks above as a normal label.
 */
function LabeledTextField({
    isMobile,
    id,
    label,
    ...inputProps
}: {
    isMobile: boolean;
    id: string;
    label: string;
} & React.ComponentProps<typeof Input>): React.ReactElement {
    if (isMobile) {
        return (
            <InputGroup className="h-11 gap-4 shrink-0">
                <InputGroupAddon align="inline-start">
                    <Text
                        variant="label"
                        render={<span />}
                        className="text-foreground-base"
                    >
                        {label}
                    </Text>
                </InputGroupAddon>
                <InputGroupInput id={id} {...inputProps} />
            </InputGroup>
        );
    }
    return (
        <LabeledField id={id} label={label}>
            <Input id={id} surface="filled" size="md" {...inputProps} />
        </LabeledField>
    );
}

/* ── Field row ──────────────────────────────────────────────── */

function LabeledField({
    id,
    label,
    children
}: {
    id: string;
    label: string;
    children: React.ReactNode;
}): React.ReactElement {
    return (
        <div className="flex flex-col gap-1.5">
            <Label htmlFor={id} className="inline-flex w-fit items-start">
                <Text
                    variant="label"
                    render={<span />}
                    className="text-foreground-base"
                >
                    {label}
                </Text>
            </Label>
            {children}
        </div>
    );
}
